import { useEffect, useState, useCallback } from 'react'
import { HubConnection } from '@microsoft/signalr'
import { signalRService } from '@services/signalr.service'
import { useAuthStore } from '@store/authStore'
import { tokenStorage } from '@services/tokenStorage'
import { isFirebaseChatEnabled } from '@services/firebase'
import type { ChatMessageDto, TypingIndicator } from '@api/types/chat'

// SignalR event types for group membership changes
export interface GroupMembershipChangedEvent {
  groupId: string
  action: 'removed' | 'left' | 'banned' | 'role_changed'
  reason: string | null
  timestamp: string
}

export interface MemberRemovedEvent {
  groupId: string
  userId: string
  action: 'removed' | 'left' | 'banned'
  timestamp: string
}

export interface RemovedFromConversationEvent {
  conversationId: string
  reason: string
  timestamp: string
}

export interface ParticipantRemovedEvent {
  conversationId: string
  userId: string
  reason: string
  timestamp: string
}

export interface GroupNotificationEvent {
  type: string
  groupId: string
  accessChatLevel?: number
}

// Signal for conversation updates (lightweight notification to fetch from API)
export interface ConversationUpdateSignal {
  conversationId: string
  updateType: 'NewMessage' | 'MessageDeleted' | 'MessagesRead' | 'ReactionChanged' | 'ParticipantLeft'
  senderId: string
  timestamp: string
}

// Legacy adventure-specific chat hook
export function useChatConnection() {
  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const { token } = useAuthStore()

  useEffect(() => {
    // Chat moved to Firebase/Firestore — don't open the SignalR chat hub when
    // Firebase is enabled (consumers guard on isConnected, so they go inert).
    if (isFirebaseChatEnabled) return
    if (!token) return

    const connect = async () => {
      try {
        const conn = await signalRService.startChatConnection(token)
        setConnection(conn)
        setIsConnected(true)
      } catch (error) {
        console.error('Failed to connect to chat hub:', error)
        setIsConnected(false)
      }
    }

    connect()

    return () => {
      signalRService.stopChatConnection()
      setIsConnected(false)
    }
  }, [token])

  const sendMessage = useCallback(
    async (adventureId: string, message: string) => {
      if (!connection || !isConnected) {
        throw new Error('Not connected to chat hub')
      }

      await connection.invoke('SendMessage', adventureId, message)
    },
    [connection, isConnected]
  )

  const onMessageReceived = useCallback(
    (callback: (user: string, message: string, timestamp: string) => void) => {
      if (!connection) return

      connection.on('ReceiveMessage', callback)

      return () => {
        connection.off('ReceiveMessage', callback)
      }
    },
    [connection]
  )

  return {
    connection,
    isConnected,
    sendMessage,
    onMessageReceived,
  }
}

// New general chat hub hook for conversation-based chat
export function useGeneralChatConnection() {
  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    // Chat moved to Firebase/Firestore — don't open the SignalR chat hub when
    // Firebase is enabled. All consumers guard on isConnected, so their SignalR
    // effects (message/typing/conversation-update handlers) become no-ops and
    // the hub/chat WebSocket is never established.
    if (isFirebaseChatEnabled) return

    const token = tokenStorage.getAccessToken()

    if (!token || !isAuthenticated) {
      return
    }

    const connect = async () => {
      try {
        const conn = await signalRService.startChatConnection(token)
        setConnection(conn)
        setIsConnected(true)
      } catch (error) {
        console.error('Failed to connect to general chat hub:', error)
        setIsConnected(false)
      }
    }

    connect()

    return () => {
      setIsConnected(false)
      setConnection(null)
    }
  }, [isAuthenticated])

  // Join a conversation room
  const joinConversation = useCallback(
    async (conversationId: string) => {
      console.log('[SignalR] joinConversation called:', {
        conversationId,
        hasConnection: !!connection,
        isConnected,
        connectionState: connection?.state,
      })

      if (!connection || !isConnected) {
        console.warn('[SignalR] Cannot join conversation - not connected')
        return
      }

      try {
        console.log('[SignalR] Invoking JoinConversation on hub...')
        await connection.invoke('JoinConversation', conversationId)
        console.log('[SignalR] Successfully joined conversation:', conversationId)
      } catch (error) {
        console.error('[SignalR] Failed to join conversation:', error)
      }
    },
    [connection, isConnected]
  )

  // Debug: Log ALL incoming SignalR messages
  useEffect(() => {
    if (!connection) return

    console.log('[SignalR] Setting up global message debugger')

    // List of known events to monitor
    const eventsToMonitor = [
      'ReceiveNewMessage',
      'ReceiveMessage',
      'ReceiveDirectMessage',
      'ReceiveGroupMessage',
      'ReceiveConversationMessage',
      'MessageUpdated',
      'TypingIndicator',
      'UserStatusChanged',
      'ConversationUpdated',
      'GroupMembershipChanged',
      'MemberRemoved',
      'RemovedFromConversation',
      'ParticipantRemoved',
      'ReceiveGroupNotification',
      'ConversationUpdateSignal',
      'UserJoinedConversation',
      'UserLeftConversation',
    ]

    const debugHandlers: Array<() => void> = []

    eventsToMonitor.forEach(eventName => {
      const handler = (...args: any[]) => {
        console.log(`[SignalR DEBUG] Event "${eventName}" received:`, args)
      }
      connection.on(eventName, handler)
      debugHandlers.push(() => connection.off(eventName, handler))
    })

    return () => {
      console.log('[SignalR] Cleaning up global message debugger')
      debugHandlers.forEach(cleanup => cleanup())
    }
  }, [connection])

  // Leave a conversation room
  const leaveConversation = useCallback(
    async (conversationId: string) => {
      if (!connection || !isConnected) return
      try {
        await connection.invoke('LeaveConversation', conversationId)
      } catch (error) {
        console.error('Failed to leave conversation:', error)
      }
    },
    [connection, isConnected]
  )

  // Send typing indicator
  const sendTypingIndicator = useCallback(
    async (conversationId: string, isTyping: boolean) => {
      // Silent no-op when the chat hub isn't connected (e.g. Firebase chat is on,
      // so hub/chat is never opened) — callers fire this on every keystroke, so
      // don't log.
      if (!connection || !isConnected) return
      try {
        await connection.invoke('SendTypingIndicator', conversationId, isTyping)
      } catch (error) {
        console.error('[SignalR] Failed to send typing indicator:', error)
      }
    },
    [connection, isConnected]
  )

  // Send message to adventure chat
  const sendMessageToAdventure = useCallback(
    async (adventureId: string, message: string) => {
      if (!connection || !isConnected) return
      try {
        await connection.invoke('SendMessageToAdventure', adventureId, message)
      } catch (error) {
        console.error('Failed to send adventure message:', error)
      }
    },
    [connection, isConnected]
  )

  // Send direct message to user
  const sendDirectMessage = useCallback(
    async (recipientId: string, message: string) => {
      if (!connection || !isConnected) return
      try {
        await connection.invoke('SendDirectMessage', recipientId, message)
      } catch (error) {
        console.error('Failed to send direct message:', error)
      }
    },
    [connection, isConnected]
  )

  // Send message to group chat
  const sendMessageToGroup = useCallback(
    async (groupId: string, message: string) => {
      if (!connection || !isConnected) return
      try {
        await connection.invoke('SendMessageToGroup', groupId, message)
      } catch (error) {
        console.error('Failed to send group message:', error)
      }
    },
    [connection, isConnected]
  )

  // Send message to conversation
  const sendMessageToConversation = useCallback(
    async (conversationId: string, message: string) => {
      console.log('[SignalR] sendMessageToConversation:', { conversationId, messageLength: message.length, hasConnection: !!connection, isConnected })
      if (!connection || !isConnected) {
        console.warn('[SignalR] Cannot send message - not connected')
        return
      }
      try {
        await connection.invoke('SendMessageToConversation', conversationId, message)
        console.log('[SignalR] Message sent successfully via SignalR')
      } catch (error) {
        console.error('[SignalR] Failed to send conversation message:', error)
      }
    },
    [connection, isConnected]
  )

  // Listen for new messages (generic)
  const onNewMessage = useCallback(
    (callback: (message: ChatMessageDto) => void) => {
      if (!connection) return

      connection.on('ReceiveNewMessage', callback)

      return () => {
        connection.off('ReceiveNewMessage', callback)
      }
    },
    [connection]
  )

  // Listen for adventure messages
  const onReceiveMessage = useCallback(
    (callback: (message: ChatMessageDto) => void) => {
      if (!connection) return

      connection.on('ReceiveMessage', callback)

      return () => {
        connection.off('ReceiveMessage', callback)
      }
    },
    [connection]
  )

  // Listen for direct messages
  const onReceiveDirectMessage = useCallback(
    (callback: (message: ChatMessageDto) => void) => {
      if (!connection) return

      connection.on('ReceiveDirectMessage', callback)

      return () => {
        connection.off('ReceiveDirectMessage', callback)
      }
    },
    [connection]
  )

  // Listen for group messages
  const onReceiveGroupMessage = useCallback(
    (callback: (message: ChatMessageDto) => void) => {
      if (!connection) return

      connection.on('ReceiveGroupMessage', callback)

      return () => {
        connection.off('ReceiveGroupMessage', callback)
      }
    },
    [connection]
  )

  // Listen for conversation messages
  const onReceiveConversationMessage = useCallback(
    (callback: (message: ChatMessageDto) => void) => {
      if (!connection) return

      connection.on('ReceiveConversationMessage', callback)

      return () => {
        connection.off('ReceiveConversationMessage', callback)
      }
    },
    [connection]
  )

  // Listen for message updates (edits, deletes)
  const onMessageUpdated = useCallback(
    (callback: (message: ChatMessageDto) => void) => {
      if (!connection) return

      connection.on('MessageUpdated', callback)

      return () => {
        connection.off('MessageUpdated', callback)
      }
    },
    [connection]
  )

  // Listen for typing indicators
  const onTypingIndicator = useCallback(
    (callback: (indicator: TypingIndicator) => void) => {
      if (!connection) return

      connection.on('TypingIndicator', callback)

      return () => {
        connection.off('TypingIndicator', callback)
      }
    },
    [connection]
  )

  // Listen for user online status changes
  const onUserStatusChanged = useCallback(
    (callback: (userId: string, isOnline: boolean) => void) => {
      if (!connection) return

      connection.on('UserStatusChanged', callback)

      return () => {
        connection.off('UserStatusChanged', callback)
      }
    },
    [connection]
  )

  // Listen for conversation updates (new participants, name changes, etc.)
  const onConversationUpdated = useCallback(
    (callback: (conversationId: string) => void) => {
      if (!connection) return

      connection.on('ConversationUpdated', callback)

      return () => {
        connection.off('ConversationUpdated', callback)
      }
    },
    [connection]
  )

  // Listen for group membership changes (sent to affected user)
  const onGroupMembershipChanged = useCallback(
    (callback: (event: GroupMembershipChangedEvent) => void) => {
      if (!connection) return

      connection.on('GroupMembershipChanged', callback)

      return () => {
        connection.off('GroupMembershipChanged', callback)
      }
    },
    [connection]
  )

  // Listen for member removed events (sent to other group members)
  const onMemberRemoved = useCallback(
    (callback: (event: MemberRemovedEvent) => void) => {
      if (!connection) return

      connection.on('MemberRemoved', callback)

      return () => {
        connection.off('MemberRemoved', callback)
      }
    },
    [connection]
  )

  // Listen for being removed from a conversation
  const onRemovedFromConversation = useCallback(
    (callback: (event: RemovedFromConversationEvent) => void) => {
      if (!connection) return

      connection.on('RemovedFromConversation', callback)

      return () => {
        connection.off('RemovedFromConversation', callback)
      }
    },
    [connection]
  )

  // Listen for participant removed events (sent to other participants)
  const onParticipantRemoved = useCallback(
    (callback: (event: ParticipantRemovedEvent) => void) => {
      if (!connection) return

      connection.on('ParticipantRemoved', callback)

      return () => {
        connection.off('ParticipantRemoved', callback)
      }
    },
    [connection]
  )

  // Listen for group notifications (permission changes, etc.)
  const onGroupNotification = useCallback(
    (callback: (event: GroupNotificationEvent) => void) => {
      if (!connection) return

      connection.on('ReceiveGroupNotification', callback)

      return () => {
        connection.off('ReceiveGroupNotification', callback)
      }
    },
    [connection]
  )

  // Listen for conversation update signals (lightweight notification to fetch from API)
  const onConversationUpdateSignal = useCallback(
    (callback: (signal: ConversationUpdateSignal) => void) => {
      if (!connection) {
        console.warn('[SignalR] Cannot register ConversationUpdateSignal - no connection')
        return
      }

      console.log('[SignalR] Registering ConversationUpdateSignal listener')

      const wrappedCallback = (signal: ConversationUpdateSignal) => {
        console.log('[SignalR] ConversationUpdateSignal received:', signal)
        callback(signal)
      }

      connection.on('ConversationUpdateSignal', wrappedCallback)

      return () => {
        console.log('[SignalR] Unregistering ConversationUpdateSignal listener')
        connection.off('ConversationUpdateSignal', wrappedCallback)
      }
    },
    [connection]
  )

  // Listen for when user is added to a new conversation
  const onUserJoinedConversation = useCallback(
    (callback: (event: { conversationId: string }) => void) => {
      if (!connection) return
      connection.on('UserJoinedConversation', callback)
      return () => {
        connection.off('UserJoinedConversation', callback)
      }
    },
    [connection]
  )

  // Leave a group chat (call when removed from group)
  const leaveGroupChat = useCallback(
    async (groupId: string) => {
      if (!connection || !isConnected) return
      try {
        await connection.invoke('LeaveGroupChat', groupId)
      } catch (error) {
        console.error('Failed to leave group chat:', error)
      }
    },
    [connection, isConnected]
  )

  return {
    connection,
    isConnected,
    joinConversation,
    leaveConversation,
    sendTypingIndicator,
    // Send methods
    sendMessageToAdventure,
    sendDirectMessage,
    sendMessageToGroup,
    sendMessageToConversation,
    // Message listeners
    onNewMessage,
    onReceiveMessage,
    onReceiveDirectMessage,
    onReceiveGroupMessage,
    onReceiveConversationMessage,
    onMessageUpdated,
    onTypingIndicator,
    onUserStatusChanged,
    onConversationUpdated,
    // Group membership events
    onGroupMembershipChanged,
    onMemberRemoved,
    onRemovedFromConversation,
    onParticipantRemoved,
    onGroupNotification,
    leaveGroupChat,
    // Conversation update signal
    onConversationUpdateSignal,
    // User joined/added to conversation
    onUserJoinedConversation,
  }
}

export function useNotificationConnection() {
  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    // Get token from tokenStorage (which has localStorage fallback)
    const token = tokenStorage.getAccessToken()

    if (!token || !isAuthenticated) {
      console.log('⏸️ No auth token available for SignalR notification connection')
      console.log('   - Token exists:', !!token)
      console.log('   - Is authenticated:', isAuthenticated)
      return
    }

    const connect = async () => {
      try {
        console.log('🔄 Initializing notification connection...')
        console.log('   - Using token from tokenStorage')
        const conn = await signalRService.startNotificationConnection(token)
        setConnection(conn)
        setIsConnected(true)
        console.log('✅ Notification connection state updated: connected')
      } catch (error) {
        console.error('❌ Failed to connect to notification hub:', error)
        setIsConnected(false)
      }
    }

    connect()

    // Cleanup only disconnects on unmount, not on re-render
    return () => {
      console.log('🔌 Component unmounting, keeping SignalR connection alive')
      setIsConnected(false)
      setConnection(null)
    }
  }, [isAuthenticated])

  const onNotificationReceived = useCallback(
    (callback: (notification: any) => void) => {
      if (!connection) {
        console.warn('⚠️ Cannot register notification listener: connection not established')
        return
      }

      console.log('👂 Registering receivenotification listener (lowercase)')

      // Wrap callback with logging
      const wrappedCallback = (notification: any) => {
        console.log('🔔 NOTIFICATION RECEIVED via SignalR:', notification)
        console.log('📦 Notification Type:', typeof notification)
        console.log('📄 Notification Data:', JSON.stringify(notification, null, 2))
        callback(notification)
      }

      // Use lowercase to match server method name
      connection.on('receivenotification', wrappedCallback)

      return () => {
        console.log('🔇 Unregistering receivenotification listener')
        connection.off('receivenotification', wrappedCallback)
      }
    },
    [connection]
  )

  // Listen for notice deleted events { Type: "NoticeDeleted", NoticeId: "..." }
  const onNoticeDeleted = useCallback(
    (callback: (event: { NoticeId: string }) => void) => {
      if (!connection) return

      const handler = (event: any) => {
        console.log('[SignalR] NoticeDeleted received:', event)
        callback(event)
      }

      connection.on('NoticeDeleted', handler)

      return () => {
        connection.off('NoticeDeleted', handler)
      }
    },
    [connection]
  )

  return {
    connection,
    isConnected,
    onNotificationReceived,
    onNoticeDeleted,
  }
}
